// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Doctor Who Universe - Star Systems Configuration
 * 
 * Wibbly-wobbly, timey-wimey locations from across time and space.
 */

export const starSystems = [
  {
    id: 900,
    name: 'Gallifrey',
    type: 'planet',
    distance: 0,
    coordinates: { x: 45, y: 50, z: 0 }, // Visual center
    threat: 'MODERATE',
    resources: 'TIME TECHNOLOGY',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons', 'upgrades'],
    tradeType: 'station_advanced',
    primaryColor: '#d97706',
    secondaryColor: '#fbbf24',
    hasRings: false,
    description: 'Homeworld of the Time Lords. Shining under the burnt orange sky. Capitol of temporal civilization.',
    scanDescription: 'Time Lord civilization detected. Temporal technology beyond comprehension. The Citadel stands eternal.'
  },
  {
    id: 901,
    name: 'Skaro',
    type: 'planet',
    distance: 20,
    coordinates: { x: 62, y: 58, z: 5 },
    threat: 'EXTREME',
    resources: 'DALEKANIUM',
    services: ['refuel', 'exploration'],
    tradeType: 'industrial',
    primaryColor: '#78716c',
    secondaryColor: '#a8a29e',
    hasRings: false,
    description: 'Homeworld of the Daleks. Ravaged by war. The Dalek City stands as a monument to hatred.',
    scanDescription: 'EXTERMINATE! EXTERMINATE! Dalek presence detected. Extreme danger. Recommend immediate retreat.'
  },
  {
    id: 902,
    name: 'Trenzalore',
    type: 'planet',
    distance: 25,
    coordinates: { x: 30, y: 42, z: -8 },
    threat: 'HIGH',
    resources: 'MEDIUM',
    services: ['refuel', 'exploration', 'trade'],
    tradeType: 'remote',
    primaryColor: '#6b7280',
    secondaryColor: '#9ca3af',
    hasRings: false,
    description: 'The Doctor\'s final resting place. Site of the Siege of Trenzalore. Time scar present.',
    scanDescription: 'Temporal anomaly detected. The Doctor\'s grave. Time scar. Question that must never be answered.'
  },
  {
    id: 903,
    name: 'Mondas',
    type: 'planet',
    distance: 18,
    coordinates: { x: 55, y: 65, z: 8 },
    threat: 'EXTREME',
    resources: 'CYBERTECHNOLOGY',
    services: ['refuel', 'exploration', 'repairs'],
    tradeType: 'industrial',
    primaryColor: '#3b82f6',
    secondaryColor: '#60a5fa',
    hasRings: false,
    description: 'Homeworld of the Cybermen. Earth\'s twin planet. You will be upgraded. Resistance is futile.',
    scanDescription: 'Cybermen detected. Conversion technology active. DELETE. DELETE. Emotional inhibitors engaged.'
  },
  {
    id: 904,
    name: 'Sontar',
    type: 'planet',
    distance: 22,
    coordinates: { x: 38, y: 60, z: -5 },
    threat: 'HIGH',
    resources: 'MILITARY',
    services: ['refuel', 'exploration', 'weapons', 'trade'],
    tradeType: 'industrial',
    primaryColor: '#16a34a',
    secondaryColor: '#4ade80',
    hasRings: false,
    description: 'Homeworld of the Sontaran Empire. Clone warriors bred for battle. Glory to Sontar!',
    scanDescription: 'Sontaran military presence. Clone production facilities. Warrior culture. Approach with caution.'
  },
  {
    id: 905,
    name: 'The Library',
    type: 'station',
    distance: 28,
    coordinates: { x: 65, y: 45, z: -10 },
    threat: 'EXTREME',
    resources: 'KNOWLEDGE',
    services: ['refuel', 'exploration', 'trade'],
    tradeType: 'luxury',
    primaryColor: '#8b5cf6',
    secondaryColor: '#a78bfa',
    hasRings: false,
    description: 'Largest library in the universe. Count the shadows. The Vashta Nerada hunt in the darkness.',
    scanDescription: 'Every book ever written. Shadows moving. Vashta Nerada infestation. Hey, who turned out the lights?'
  },
  {
    id: 906,
    name: 'Ood Sphere',
    type: 'planet',
    distance: 15,
    coordinates: { x: 50, y: 35, z: 3 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'trade'],
    tradeType: 'agricultural',
    primaryColor: '#e0f2fe',
    secondaryColor: '#bae6fd',
    hasRings: false,
    description: 'Homeworld of the Ood. Peaceful telepathic species. The song of the Ood echoes across the snow.',
    scanDescription: 'Ood civilization. Telepathic network detected. Peaceful. The song is beautiful. Trade available.'
  },
  {
    id: 907,
    name: 'Raxacoricofallapatorius',
    type: 'planet',
    distance: 30,
    coordinates: { x: 28, y: 55, z: 10 },
    threat: 'MODERATE',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'luxury',
    primaryColor: '#22c55e',
    secondaryColor: '#86efac',
    hasRings: false,
    description: 'Homeworld of the Slitheen and Blathereen families. Beautiful paradise world. Beware of gas exchange.',
    scanDescription: 'Raxacoricofallapatorian civilization. Advanced technology. Compression field detected. Vinegar present.'
  },
  {
    id: 908,
    name: 'Midnight',
    type: 'planet',
    distance: 35,
    coordinates: { x: 70, y: 52, z: -12 },
    threat: 'EXTREME',
    resources: 'DIAMONDS',
    services: ['refuel', 'exploration'],
    tradeType: 'mining',
    primaryColor: '#0ea5e9',
    secondaryColor: '#38bdf8',
    hasRings: false,
    description: 'Diamond planet bathed in deadly Xtonic radiation. Something lives in the light. Don\'t look away.',
    scanDescription: 'Xtonic radiation. Sapphire waterfalls. Unknown entity detected. It\'s looking at you. It\'s inside.'
  },
  {
    id: 909,
    name: 'The Medusa Cascade',
    type: 'station',
    distance: 32,
    coordinates: { x: 42, y: 68, z: 6 },
    threat: 'HIGH',
    resources: 'TEMPORAL RIFT',
    services: ['refuel', 'repairs'],
    tradeType: 'remote',
    primaryColor: '#ec4899',
    secondaryColor: '#f9a8d4',
    hasRings: true,
    description: 'Rift in time and space. The Daleks hid Earth here. Reality is thin. Time is in flux.',
    scanDescription: 'Temporal rift. Reality breach. 27 stolen planets detected. Dalek activity. The stars are going out.'
  },
  {
    id: 910,
    name: 'New Earth',
    type: 'planet',
    distance: 12,
    coordinates: { x: 52, y: 48, z: -3 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'upgrades'],
    tradeType: 'luxury',
    primaryColor: '#3b82f6',
    secondaryColor: '#93c5fd',
    hasRings: false,
    description: 'Humanity\'s new homeworld in the far future. New New York shines under alien skies. The Face of Boe watches.',
    scanDescription: 'Human colony. Advanced medical technology. New New York. The Sisters of Plenitude. Traffic jam eternal.'
  },
  {
    id: 911,
    name: 'The Pandorica',
    type: 'station',
    distance: 26,
    coordinates: { x: 48, y: 40, z: -7 },
    threat: 'EXTREME',
    resources: 'PRISON',
    services: ['refuel'],
    tradeType: 'remote',
    primaryColor: '#1f2937',
    secondaryColor: '#4b5563',
    hasRings: false,
    description: 'Perfect prison built to contain the most feared being in the universe. The Pandorica will open.',
    scanDescription: 'Ancient prison. Alliance of enemies. The Pandorica opens. Silence will fall. The universe is ending.'
  }
];

export const starPositions = starSystems.reduce((acc, system) => {
  acc[system.id] = system.coordinates;
  return acc;
}, {});

const doctorwhoSystems = { starSystems, starPositions };
export default doctorwhoSystems;
